// Het ontwerp uit de webshop-editor in de widget (akkoord Martijn 13-10-2026).
//
//  1. Ophalen: de gepubliceerde stand van deze webshopwidget, of in de editor en bij "Test je webshop" (concept=1) het concept;
//     de api geeft het concept alleen met een geldig bewerkteken. Geen ontwerp = de webshop zoals altijd.
//  2. Toepassen: de eigen kleuren over de opmaak, de hoekafronding in de opmaak van de widget, de welkomsttekst in de
//     webshopinstellingen en de webshop-opties. Steeds over wat de server stuurde (de "basis"), zodat een wijziging in de editor
//     ook terug kan (Eigen kleuren weer uit = de kleuren van de server).
//  3. De bewerkstand (alleen ?wseditor=1, in het venster van de editor): bij hover een rand om wat je kunt aanpassen (data-ws-tegel,
//     data-ws-tekst, data-ws-categorie, data-ws-product, data-ws-menu) en een potlood. Een klik op het potlood stuurt een bericht
//     naar de editor; ergens anders klikken werkt gewoon (zo kun je door je webshop lopen). Berichten van de editor alleen van de
//     pagina eromheen en van de herkomst die hij in parentOrigin meegaf.
import { Suspense, lazy, useEffect, useRef } from "react";
// Pas laden als de editor een product laat bewerken: zo blijft de widget voor gasten even klein.
const Productbewerker = lazy(() => import("../widgets/WebshopWidget/Productbewerker"));
import { FetchWebShopProductList } from "../api/Webshop.service";
import { setWebshopProductData } from "../store/slice/Webshop.slice";
import { useDispatch, useSelector } from "react-redux";
import { haalWebshopOntwerp } from "../api/Webshopontwerp.service";
import { zetOntwerp, zetBewerkProduct } from "../store/slice/Webshopontwerp.slice";
import { updateCss, updateWidgetConfig } from "../store/slice/widgetSetting.slice";
import { setWebshopCategoryData, setWebshopOpties, setWebshopSettings, updateSelectedMenuItem, updateWebShopMenuPosition } from "../store/slice/Webshop.slice";
import { bewerkdoel, isEditor, isEditorbericht, metOntwerpCategorieen, metOntwerpCss, metOntwerpHoek, metOntwerpOpties, metWelkom, wilConcept } from "../lib/webshopOntwerp";
import { normaliseerOpties } from "../lib/webshopOpties";
import { startBewerkmodus } from "../lib/bewerkmodus";
import { ouderAdres } from "../lib/bericht";
import { vertaal as jmVertaal } from "../lib/woorden";

// Het adres bij het laden: de widget navigeert daarna zonder de parameters (naar /webshopMenuItem).
const BEGIN = typeof window !== "undefined" ? window.location.search : "";

/** Gelijk van inhoud: de winkel maakt soms een nieuw object van hetzelfde (updateWidgetConfig), dat is geen nieuwe basis. */
const gelijk = (a, b) => a === b || (!!a && !!b && JSON.stringify(a) === JSON.stringify(b));

/**
 * Telkens over de basis van de server: is de waarde in de winkel niet (van inhoud) de eigen uitkomst, dan is het een nieuwe basis.
 * Alleen zetten als de uitkomst echt anders is; anders loopt het rond (elke uitkomst is een nieuw object).
 */
function useToepassen(waarde, ontwerp, maak, zet, extra = []) {
  const basis = useRef(undefined);
  const eigen = useRef(undefined);
  useEffect(() => {
    if (waarde === undefined || waarde === null) return;
    if (!gelijk(waarde, eigen.current)) basis.current = waarde;
    const nieuw = maak(basis.current, ontwerp);
    if (!gelijk(nieuw, waarde)) {
      eigen.current = nieuw;
      zet(nieuw);
    } else {
      eigen.current = waarde;
    }
    // maak en zet zijn per aanroep nieuw, maar doen hetzelfde.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waarde, ontwerp, ...extra]);
}

function stuur(bericht) {
  try {
    window.parent.postMessage({ bron: "jimani-widget", ...bericht }, ouderAdres());
  } catch { /* geen ouder */ }
}

export default function Webshopontwerp() {
  const dispatch = useDispatch();
  const { key, widgetSetupId, resType, css, widgetConfig } = useSelector((s) => s?.widgetSettings) || {};
  const { webshopSettings, defaultLanguage, langID, webshopOpties, webShopCategoryList, selectedMenuItem, webShopMenuPosition } = useSelector((s) => s?.Webshop) || {};
  const ontwerp = useSelector((s) => s?.Webshopontwerp?.ontwerp ?? null);
  const bewerkProduct = useSelector((s) => s?.Webshopontwerp?.bewerkProduct ?? null);
  const webshop = String(resType || "").toLowerCase() === "webshop";
  const editor = webshop && isEditor(BEGIN);

  // 1. Ophalen.
  useEffect(() => {
    if (!webshop || !key || !widgetSetupId) return undefined;
    let weg = false;
    const concept = wilConcept(BEGIN);
    if (concept) startBewerkmodus();
    haalWebshopOntwerp(key, widgetSetupId, concept).then((data) => {
      if (weg) return;
      dispatch(zetOntwerp({ ontwerp: data?.ontwerp ?? null, concept: !!data?.concept }));
      if (editor) stuur({ soort: "webshopontwerp-klaar" });
    });
    return () => { weg = true; };
  }, [webshop, key, widgetSetupId, editor, dispatch]);

  // 2. Toepassen.
  useToepassen(webshop ? css : null, ontwerp, metOntwerpCss, (n) => dispatch(updateCss(n)));
  useToepassen(webshop ? widgetConfig : null, ontwerp, metOntwerpHoek, (n) => dispatch(updateWidgetConfig(n)));
  useToepassen(webshop ? webshopSettings : null, ontwerp,
    (basis, o) => metWelkom(basis, o, defaultLanguage, langID), (n) => dispatch(setWebshopSettings(n)), [defaultLanguage, langID]);
  useToepassen(webshop ? webshopOpties : null, ontwerp,
    (basis, o) => { const n = metOntwerpOpties(basis, o); return n === basis ? basis : normaliseerOpties(n); },
    (n) => dispatch(setWebshopOpties(n)));
  // Stap 2: categorieen die deze webshop verbergt; staat de gekozen categorie daartussen, dan de eerste die wel zichtbaar is.
  useToepassen(webshop ? webShopCategoryList : null, ontwerp, metOntwerpCategorieen, (n) => dispatch(setWebshopCategoryData(n)));
  useEffect(() => {
    if (!webshop || !Array.isArray(webShopCategoryList) || !webShopCategoryList.length || !selectedMenuItem) return;
    if (!webShopCategoryList.some((c) => c?.idCategory === selectedMenuItem?.idCategory)) dispatch(updateSelectedMenuItem(webShopCategoryList[0]));
  }, [webshop, webShopCategoryList, selectedMenuItem, dispatch]);

  // Het menu van de webshop staat waar de webshop-opties zeggen (categorieWeergave): tabs = boven, zijbalk = links. De oude
  // keuze van de winkel (WidgetMenu, ook "eerst de categorieen") geldt niet meer voor de webshop, alleen nog voor de kiosk.
  useEffect(() => {
    if (!webshop) return;
    const gewenst = (webshopOpties?.categorieWeergave ?? "tabs") === "zijbalk" ? "vertical" : "horizontal";
    if (gewenst !== webShopMenuPosition) dispatch(updateWebShopMenuPosition(gewenst));
  }, [webshop, webshopOpties, webShopMenuPosition, dispatch]);

  // 3. De bewerkstand.
  useEffect(() => {
    if (!editor || typeof document === "undefined") return undefined;

    const stijl = document.createElement("style");
    stijl.setAttribute("data-ws-bewerkstand", "");
    stijl.textContent = `
      .ws-bewerk-rand { outline: 2px dashed #2563eb !important; outline-offset: 2px; }
      .ws-bewerk-potlood { position: fixed; z-index: 2147483000; width: 32px; height: 32px; border-radius: 50%; border: 2px solid #fff;
        background: #2563eb; color: #fff; display: none; align-items: center; justify-content: center; cursor: pointer; padding: 0;
        box-shadow: 0 2px 8px rgba(0,0,0,.25); }
      .ws-bewerk-potlood svg { width: 15px; height: 15px; fill: currentColor; }
      /* In de editor alleen het blauwe potlood: het grijze potlood van de bewerkmodus (Producttegel) is hier dubbel (13-10-2026). */
      /* En de gele balk "Bewerkmodus" boven de producten: de editor zegt dat zelf al. */
      [data-testid="tegel-bewerk"], [data-testid="bewerkmodus-balk"] { display: none !important; }
    `;
    document.head.appendChild(stijl);

    const potlood = document.createElement("button");
    potlood.type = "button";
    potlood.className = "ws-bewerk-potlood";
    potlood.setAttribute("data-ws-potlood", "");
    potlood.setAttribute("aria-label", jmVertaal("Aanpassen"));
    potlood.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>';
    document.body.appendChild(potlood);

    let huidig = null;
    const zetPlek = () => {
      if (!huidig?.element?.isConnected) { potlood.style.display = "none"; return; }
      const r = huidig.element.getBoundingClientRect();
      potlood.style.display = r.width > 0 ? "flex" : "none";
      potlood.style.top = Math.max(4, r.top + 6) + "px";
      potlood.style.left = Math.max(4, Math.min(window.innerWidth - 40, r.right - 38)) + "px";
    };
    const kies = (doel) => {
      if (huidig?.element === doel?.element) return;
      huidig?.element?.classList.remove("ws-bewerk-rand");
      huidig = doel;
      huidig?.element?.classList.add("ws-bewerk-rand");
      zetPlek();
    };
    const over = (e) => {
      if (e.target === potlood || potlood.contains(e.target)) return;
      kies(bewerkdoel(e.target));
    };
    const klik = (e) => {
      if (!(e.target === potlood || potlood.contains(e.target)) || !huidig) return;
      e.preventDefault();
      e.stopPropagation();
      stuur({ soort: "webshopontwerp-klik", doel: huidig.doel, waarde: huidig.waarde });
    };
    const verlaat = (e) => { if (!e.relatedTarget) kies(null); };

    document.addEventListener("mouseover", over, true);
    document.addEventListener("mouseout", verlaat, true);
    document.addEventListener("click", klik, true);
    window.addEventListener("scroll", zetPlek, true);
    window.addEventListener("resize", zetPlek);

    const luister = (e) => {
      if (!isEditorbericht(e, window.parent, ouderAdres())) return;
      if (e.data.soort === "webshopontwerp") dispatch(zetOntwerp({ ontwerp: e.data.ontwerp ?? null, concept: true }));
      if (e.data.soort === "webshopontwerp-product") dispatch(zetBewerkProduct(Number(e.data.idproduct) || null));
    };
    window.addEventListener("message", luister);

    return () => {
      document.removeEventListener("mouseover", over, true);
      document.removeEventListener("mouseout", verlaat, true);
      document.removeEventListener("click", klik, true);
      window.removeEventListener("scroll", zetPlek, true);
      window.removeEventListener("resize", zetPlek);
      window.removeEventListener("message", luister);
      huidig?.element?.classList.remove("ws-bewerk-rand");
      potlood.remove();
      stijl.remove();
    };
  }, [editor, dispatch]);

  // Een product dat de editor laat bewerken (knop in het paneel, stap 3): de Productbewerker, waar je ook in de webshop bent. Na
  // opslaan de lijst van de gekozen categorie opnieuw ophalen.
  if (!editor || !bewerkProduct) return null;
  return (
    <Suspense fallback={null}><Productbewerker
      open
      item={{ idproduct: bewerkProduct }}
      sleutel={key}
      taal={defaultLanguage}
      onSluit={() => dispatch(zetBewerkProduct(null))}
      onOpgeslagen={async () => {
        try {
          if (selectedMenuItem?.idCategory == null) return;
          const antwoord = await FetchWebShopProductList(key, defaultLanguage, selectedMenuItem.idCategory);
          if (antwoord?.status === 200) dispatch(setWebshopProductData(antwoord.data));
        } catch { /* de lijst blijft zoals hij was */ }
        stuur({ soort: "webshopontwerp-opgeslagen", idproduct: bewerkProduct });
      }}
    /></Suspense>
  );
}
